import {
  Briefcase,
  CalendarDays,
  ClipboardList,
  Archive,
  ChevronRight,
  CircleDot,
  DollarSign,
  Folder,
  Handshake,
  LineChart,
  List,
  Scale,
  ScrollText,
  Settings,
  SquareCheck,
  Tag,
  UserRound,
  Users,
  Workflow,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

import {
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import { MatterDetailSubMenu } from "./MatterDetailSubMenu";
import { PAGE_PERMISSION_KEYS } from "@/lib/data/static/permissionOptions";
import { cn } from "@/lib/utils/cn";
import { Link } from "@tanstack/react-router";

const COSTS_PATH = "/matters/settings/costs";
const SCHEDULED_MESSAGES_PATH = "/matters/settings/scheduled-messages";
const INTAKE_TEMPLATES_PATH = "/matters/settings/intake-templates";
const STATUSES_PATH = "/matters/settings/statuses";
const TASK_PLANS_PATH = "/matters/settings/task-plans";
const PHASE_PROJECTS_PATH = "/matters/settings/phase-projects";
const LITIGATION_PLANS_PATH = "/matters/settings/litigation-plans";
const DEFAULT_FILES_PATH = "/matters/settings/files";
const ROLES_SETTINGS_PATH = "/matters/settings/roles";
const TEAMS_SETTINGS_PATH = "/matters/settings/teams";
const INCIDENT_TYPES_PATH = "/matters/settings/matter-types";
const TAGS_PATH = "/matters/settings/tags";
const MATTER_SETTINGS_PATH = "/matters/settings";
const MY_MATTERS_PATH = "/matters/my-matters";
const ALL_MATTERS_PATH = "/matters";
const LEGACY_MATTER_SETTINGS_PATHS = ["/settings/matter-defaults"] as const;
const MATTER_REPORT_PATHS = [
  "/reports/case-assignments",
  "/reports/open-matters",
  "/reports/closed-matters",
  "/reports/demands",
  "/reports/demand-summary",
  "/reports/settlements",
  "/reports/settlement-summary",
  "/reports/attorney-summary",
  "/reports/record-requests",
  "/reports/approaching-statutes",
  "/reports/matter-activity",
  "/reports/matter-status-count",
  "/reports/role-assignments",
] as const;

/** Single-line labels in the sub-nav; ellipsis only if the sidebar is too narrow. */
const matterSubNavLabelClass =
  "[&>span:last-child]:min-w-0 [&>span:last-child]:whitespace-nowrap [&>span:last-child]:overflow-hidden [&>span:last-child]:text-ellipsis";

type MatterNestedMenuItem = {
  title: string;
  url: string;
  icon: LucideIcon;
};

const isMatterSettingsPath = (path: string) =>
  path.startsWith(MATTER_SETTINGS_PATH) ||
  LEGACY_MATTER_SETTINGS_PATHS.some(
    (settingsPath) =>
      path === settingsPath || path.startsWith(`${settingsPath}/`),
  );

const isMatterReportsPath = (path: string) =>
  MATTER_REPORT_PATHS.some(
    (reportPath) => path === reportPath || path.startsWith(`${reportPath}/`),
  );

export const MattersSubMenu = ({
  matterId,
  currentPath,
}: {
  matterId?: string;
  currentPath: string;
}) => {
  const { hasPagePermission } = useUser();
  const { setOpenMobile } = useSidebar();
  const [matterSettingsOpen, setMatterSettingsOpen] = useState(() =>
    isMatterSettingsPath(currentPath),
  );
  const [matterReportsOpen, setMatterReportsOpen] = useState(() =>
    isMatterReportsPath(currentPath),
  );
  const prevPathForSettingsRef = useRef(currentPath);

  useEffect(() => {
    const prev = prevPathForSettingsRef.current;
    prevPathForSettingsRef.current = currentPath;
    if (!isMatterSettingsPath(prev) && isMatterSettingsPath(currentPath)) {
      setMatterSettingsOpen(true);
    }
    if (!isMatterReportsPath(prev) && isMatterReportsPath(currentPath)) {
      setMatterReportsOpen(true);
    }
  }, [currentPath]);
  const canViewCosts = hasPagePermission(
    PAGE_PERMISSION_KEYS.mattersSettingsCosts,
  );
  const canViewStatuses = hasPagePermission(
    PAGE_PERMISSION_KEYS.mattersSettingsStatuses,
  );
  const canViewTaskPlans = hasPagePermission(
    PAGE_PERMISSION_KEYS.mattersSettingsTaskPlans,
  );
  const canViewPhaseProjects = hasPagePermission(
    PAGE_PERMISSION_KEYS.mattersSettingsPhaseProjects,
  );
  const canViewLitigationSettings = hasPagePermission(
    PAGE_PERMISSION_KEYS.mattersSettingsLitigationPlans,
  );
  const canViewDefaultFiles = hasPagePermission(
    PAGE_PERMISSION_KEYS.mattersSettingsDefaultFiles,
  );
  const canViewIncidentTypes = hasPagePermission(
    PAGE_PERMISSION_KEYS.mattersSettingsTypes,
  );
  const canViewMatterRoles = hasPagePermission(
    PAGE_PERMISSION_KEYS.mattersSettingsRoles,
  );
  const canViewMatterTeams = hasPagePermission(
    PAGE_PERMISSION_KEYS.mattersSettingsTeams,
  );
  const canViewMatterIntakeTemplates = hasPagePermission(
    PAGE_PERMISSION_KEYS.mattersSettingsIntakeTemplates,
  );
  const canViewTags = hasPagePermission(
    PAGE_PERMISSION_KEYS.mattersSettingsTags,
  );
  const canViewMatterScheduledMessages = hasPagePermission(
    PAGE_PERMISSION_KEYS.mattersSettingsScheduledMessages,
  );
  const canViewMatterSettingsSection =
    canViewCosts ||
    canViewStatuses ||
    canViewTaskPlans ||
    canViewPhaseProjects ||
    canViewLitigationSettings ||
    canViewDefaultFiles ||
    canViewTags ||
    canViewMatterRoles ||
    canViewMatterTeams ||
    canViewMatterScheduledMessages ||
    canViewMatterIntakeTemplates ||
    canViewIncidentTypes;
  const canViewDemandReport = hasPagePermission(
    PAGE_PERMISSION_KEYS.mattersReportsDemands,
  );
  const canViewRecordRequestsReport = hasPagePermission(
    PAGE_PERMISSION_KEYS.mattersReportsRecordRequests,
  );
  const canViewSettlementReport = hasPagePermission(
    PAGE_PERMISSION_KEYS.mattersReportsSettlements,
  );
  const canViewApproachingStatutesReport = hasPagePermission(
    PAGE_PERMISSION_KEYS.mattersReportsApproachingStatutes,
  );
  const canViewMatterActivityReport = hasPagePermission(
    PAGE_PERMISSION_KEYS.mattersReportsActivity,
  );
  const canViewMatterStatusCountReport = hasPagePermission(
    PAGE_PERMISSION_KEYS.mattersReportsPerStatus,
  );
  const canViewRoleAssignmentsReport = hasPagePermission(
    PAGE_PERMISSION_KEYS.mattersReportsRoleAssignments,
  );
  const canViewClosedMattersReport = hasPagePermission(
    PAGE_PERMISSION_KEYS.mattersClosed,
  );
  const matterReportsItems: MatterNestedMenuItem[] = [
    canViewClosedMattersReport
      ? {
          title: "Closed matters",
          url: "/reports/closed-matters",
          icon: Archive,
        }
      : null,
    canViewRoleAssignmentsReport && canViewSettlementReport
      ? {
          title: "Case assignments",
          url: "/reports/case-assignments",
          icon: UserRound,
        }
      : null,
    canViewDemandReport
      ? { title: "Demands", url: "/reports/demands", icon: ScrollText }
      : null,
    canViewDemandReport
      ? {
          title: "Daily demands",
          url: "/reports/demand-summary",
          icon: ScrollText,
        }
      : null,
    canViewSettlementReport && canViewRoleAssignmentsReport
      ? { title: "Attorneys", url: "/reports/attorney-summary", icon: Users }
      : null,
    canViewSettlementReport
      ? {
          title: "Settlements",
          url: "/reports/settlement-summary",
          icon: Handshake,
        }
      : null,
    canViewRecordRequestsReport
      ? {
          title: "Record requests",
          url: "/reports/record-requests",
          icon: ClipboardList,
        }
      : null,
    canViewApproachingStatutesReport
      ? {
          title: "Approaching statutes",
          url: "/reports/approaching-statutes",
          icon: CalendarDays,
        }
      : null,
    canViewMatterActivityReport
      ? {
          title: "Matter activity",
          url: "/reports/matter-activity",
          icon: SquareCheck,
        }
      : null,
    canViewMatterStatusCountReport
      ? {
          title: "Matters per status",
          url: "/reports/matter-status-count",
          icon: LineChart,
        }
      : null,
    canViewRoleAssignmentsReport
      ? {
          title: "Role assignments",
          url: "/reports/role-assignments",
          icon: UserRound,
        }
      : null,
  ].filter((item): item is MatterNestedMenuItem => Boolean(item));
  const matterSettingsItems: MatterNestedMenuItem[] = [
    canViewMatterScheduledMessages
      ? {
          title: "Scheduled messages",
          url: SCHEDULED_MESSAGES_PATH,
          icon: CalendarDays,
        }
      : null,
    canViewMatterIntakeTemplates
      ? {
          title: "Intake templates",
          url: INTAKE_TEMPLATES_PATH,
          icon: CircleDot,
        }
      : null,
    canViewMatterRoles
      ? { title: "Roles", url: ROLES_SETTINGS_PATH, icon: UserRound }
      : null,
    canViewMatterTeams
      ? { title: "Teams", url: TEAMS_SETTINGS_PATH, icon: Users }
      : null,
    canViewIncidentTypes
      ? { title: "Incident types", url: INCIDENT_TYPES_PATH, icon: Briefcase }
      : null,
    canViewStatuses
      ? { title: "Statuses", url: STATUSES_PATH, icon: CircleDot }
      : null,
    canViewTags ? { title: "Tags", url: TAGS_PATH, icon: Tag } : null,
    canViewCosts ? { title: "Costs", url: COSTS_PATH, icon: DollarSign } : null,
    canViewTaskPlans
      ? { title: "Task plans", url: TASK_PLANS_PATH, icon: SquareCheck }
      : null,
    canViewPhaseProjects
      ? { title: "Matter phases", url: PHASE_PROJECTS_PATH, icon: Workflow }
      : null,
    canViewLitigationSettings
      ? {
          title: "Litigation plans",
          url: LITIGATION_PLANS_PATH,
          icon: Scale,
        }
      : null,
    canViewDefaultFiles
      ? { title: "Default files", url: DEFAULT_FILES_PATH, icon: Folder }
      : null,
  ].filter((item): item is MatterNestedMenuItem => Boolean(item));
  const canViewReports = matterReportsItems.length > 0;
  const isAllMattersActive =
    currentPath === ALL_MATTERS_PATH || currentPath === `${ALL_MATTERS_PATH}/`;
  const isMyMattersActive =
    currentPath === MY_MATTERS_PATH || currentPath === `${MY_MATTERS_PATH}/`;
  const canViewMyMatters = hasPagePermission(PAGE_PERMISSION_KEYS.mattersMy);
  const canViewAllMatters = hasPagePermission(PAGE_PERMISSION_KEYS.mattersAll);

  return (
    <SidebarMenuSub className="mt-1.5 mr-0 ml-2 gap-1 border-l-2 border-l-neutral-900 py-0.5 pr-0 pl-3">
      {canViewMyMatters ? (
        <SidebarMenuSubItem>
          <SidebarMenuSubButton asChild className={matterSubNavLabelClass}>
            <Link
              to={MY_MATTERS_PATH}
              className={cn(
                isMyMattersActive &&
                  "bg-neutral-900 text-white hover:bg-neutral-700 active:bg-neutral-700",
              )}
              onClick={() => {
                setOpenMobile(false);
              }}
            >
              <UserRound className="mr-2 h-4 w-4 shrink-0" />
              My matters
            </Link>
          </SidebarMenuSubButton>
        </SidebarMenuSubItem>
      ) : null}
      {canViewAllMatters ? (
        <SidebarMenuSubItem>
          <SidebarMenuSubButton asChild className={matterSubNavLabelClass}>
            <Link
              to={ALL_MATTERS_PATH}
              className={cn(
                isAllMattersActive &&
                  "bg-neutral-900 text-white hover:bg-neutral-700 active:bg-neutral-700",
              )}
              onClick={() => {
                setOpenMobile(false);
              }}
            >
              <List className="mr-2 h-4 w-4 shrink-0" />
              All matters
            </Link>
          </SidebarMenuSubButton>
        </SidebarMenuSubItem>
      ) : null}
      {matterId ? (
        <MatterDetailSubMenu matterId={matterId} currentPath={currentPath} />
      ) : null}
      {canViewReports ? (
        <SidebarMenuSubItem className="flex flex-col gap-0">
          <SidebarMenuSubButton asChild className={matterSubNavLabelClass}>
            <button
              type="button"
              aria-expanded={matterReportsOpen}
              className="flex w-full items-center justify-between gap-2"
              onClick={() => {
                setMatterReportsOpen((open) => !open);
              }}
            >
              <span className="flex min-w-0 flex-1 items-center gap-2">
                <LineChart className="h-4 w-4 shrink-0" />
                <span className="min-w-0 truncate text-left">Reports</span>
              </span>
              <ChevronRight
                className={cn(
                  "h-4 w-4 shrink-0 transition-transform",
                  matterReportsOpen && "rotate-90",
                )}
                aria-hidden
              />
            </button>
          </SidebarMenuSubButton>
          {matterReportsOpen ? (
            <SidebarMenuSub className="mt-1 mr-0 ml-0 border-l-2 border-l-neutral-300 py-0.5 pr-0 pl-3">
              {matterReportsItems.map((item) => (
                <SidebarMenuSubItem key={item.url}>
                  <SidebarMenuSubButton
                    asChild
                    className={matterSubNavLabelClass}
                  >
                    <Link
                      to={item.url}
                      className={cn(
                        "flex w-full items-center gap-2 rounded-md",
                        currentPath === item.url ||
                          currentPath.startsWith(`${item.url}/`)
                          ? "bg-neutral-900 text-white hover:bg-neutral-700 active:bg-neutral-700"
                          : "",
                      )}
                      onClick={() => {
                        setOpenMobile(false);
                      }}
                    >
                      <item.icon className="h-4 w-4 shrink-0" aria-hidden />
                      <span className="min-w-0 truncate">{item.title}</span>
                    </Link>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              ))}
            </SidebarMenuSub>
          ) : null}
        </SidebarMenuSubItem>
      ) : null}
      {canViewMatterSettingsSection && matterSettingsItems.length > 0 ? (
        <SidebarMenuSubItem className="flex flex-col gap-0">
          <SidebarMenuSubButton asChild className={matterSubNavLabelClass}>
            <button
              type="button"
              aria-expanded={matterSettingsOpen}
              className="flex w-full items-center justify-between gap-2"
              onClick={() => {
                setMatterSettingsOpen((open) => !open);
              }}
            >
              <span className="flex min-w-0 flex-1 items-center gap-2">
                <Settings className="h-4 w-4 shrink-0" />
                <span className="min-w-0 truncate text-left">Settings</span>
              </span>
              <ChevronRight
                className={cn(
                  "h-4 w-4 shrink-0 transition-transform",
                  matterSettingsOpen && "rotate-90",
                )}
                aria-hidden
              />
            </button>
          </SidebarMenuSubButton>
          {matterSettingsOpen ? (
            <SidebarMenuSub className="mt-1 mr-0 ml-0 border-l-2 border-l-neutral-300 py-0.5 pr-0 pl-3">
              {matterSettingsItems.map((item) => (
                <SidebarMenuSubItem key={item.url}>
                  <SidebarMenuSubButton
                    asChild
                    className={matterSubNavLabelClass}
                  >
                    <Link
                      to={item.url}
                      className={cn(
                        "flex w-full items-center gap-2 rounded-md",
                        currentPath === item.url ||
                          currentPath.startsWith(`${item.url}/`)
                          ? "bg-neutral-900 text-white hover:bg-neutral-700 active:bg-neutral-700"
                          : "",
                      )}
                      onClick={() => {
                        setOpenMobile(false);
                      }}
                    >
                      <item.icon className="h-4 w-4 shrink-0" aria-hidden />
                      <span className="min-w-0 truncate">{item.title}</span>
                    </Link>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              ))}
            </SidebarMenuSub>
          ) : null}
        </SidebarMenuSubItem>
      ) : null}
    </SidebarMenuSub>
  );
};
